import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import {
  asyncScheduler,
  delayWhen,
  observeOn,
  of,
  race,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { RequisitionsActions, UpdateContext } from '@recruitee/requisitions-data';

import { RequisitionAutoSaveDispatcherService } from './requisition-auto-save-dispatcher.service';
import { RequisitionFormManagerService } from './requisition-form-manager.service';
import { RequisitionsNotificationsService } from './requisitions-notifications.service';
import { SingleRequisitionsActionsService } from './single-requisitions-actions.service';
import { RequisitionsDataProviderService } from '../data/single-requisition/requisitions-data-provider.service';

@Injectable()
export class RequisitionSaveService {
  private requisitionId: number;
  private isSaving = false;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private requisitionsActionsService: SingleRequisitionsActionsService,
    private requisitionsNotificationsService: RequisitionsNotificationsService,
    private requisitionFormManager: RequisitionFormManagerService,
    private requisitionsData: RequisitionsDataProviderService,
    private requisitionAutoSaveDispatcher: RequisitionAutoSaveDispatcherService,
    private actions$: Actions,
  ) {
    this.requisitionsData.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisition }) => (this.requisitionId = requisition.id));
    this.requisitionAutoSaveDispatcher.save$
      .pipe(
        observeOn(asyncScheduler),
        switchMap(() => this.save(true, UpdateContext.ChangeTab)),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(allowChangeTab =>
        this.requisitionAutoSaveDispatcher.allowChangeTab(allowChangeTab),
      );
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public save(allowToSkipSave: boolean, context: UpdateContext = UpdateContext.Default) {
    if (this.isSaving) return this.isSavingEarlyReturn(context === UpdateContext.ChangeTab);

    if (allowToSkipSave && this.requisitionFormManager.noChanges()) return of(true);

    this.requisitionFormManager.toggleFieldsRequired(false);
    if (context === UpdateContext.Default) this.requisitionFormManager.keepValidation(false);

    if (!this.requisitionFormManager.form.valid) {
      this.requisitionsNotificationsService.invalidFormFieldsError();
      return of(false);
    }

    return this.requisitionsActionsService
      .save(
        {
          ...this.requisitionFormManager.getUpdatePayload(),
          id: this.requisitionId,
        },
        context,
      )
      .pipe(
        filterBoolean,
        tap(() => (this.isSaving = true)),
        switchMap(() => this.finalizeSave()),
        tap(() => {
          this.isSaving = false;
          this.requisitionFormManager.form.markAsPristine();
        }),
        delayWhen(() => this.requisitionFormManager.updated$),
      );
  }

  private isSavingEarlyReturn(isChangingTab: boolean) {
    this.requisitionsNotificationsService.saving();
    return isChangingTab ? this.finalizeSave().pipe(tap(() => (this.isSaving = false))) : of(false);
  }

  private finalizeSave() {
    const success$ = this.actions$.pipe(
      ofType(RequisitionsActions.updateOnePessimisticSuccess),
      map(() => true),
    );
    const error$ = this.actions$.pipe(
      ofType(RequisitionsActions.updateOnePessimisticFailed),
      map(() => false),
    );
    return race(success$, error$).pipe(take(1));
  }
}
